import { ColumnState } from 'ag-grid-community';
import { DateRange } from 'react-day-picker';
import { create } from 'zustand';
import { SAL2080_R01Dto } from './Dto_SAL2080_R01';
import { getCurrentMonthDateRange } from 'src/utils/date/dateUtils';

export interface SAL2080_R01Filter {
    compCd: string;
    customCd: string;
	customNm: string;	
    taxType: string;
    taxBilling: string;
    issuedDateRange: DateRange | undefined;
    remark: string;
    fixedFlag: string;
}

/**
 * Zustand 스토어의 상태 및 액션 정의
 */
export interface Store_SAL2080_R01 {
    SAL2080_R01Filter: SAL2080_R01Filter;
    SAL2080_R01PastFilter: SAL2080_R01Filter;
    setSAL2080_R01Filter: (filter: Partial<SAL2080_R01Filter>) => void;
    setSAL2080_R01PastFilter: (filter: Partial<SAL2080_R01Filter>) => void;

    // === 리스트 관련 ===
    SAL2080_R01List: SAL2080_R01Dto[];
    setSAL2080_R01List: (list: SAL2080_R01Dto[]) => void;
    insertSAL2080_R01List: (data: SAL2080_R01Dto[], index?: number) => void;
    removeSAL2080_R01List: (index: number) => void;
    updateSAL2080_R01List: (data: SAL2080_R01Dto, index: number) => void;
    // === 선택 관련 ===
    selectedSAL2080_R01List: string[];
    setSelectedSAL2080_R01List: (idList: string[]) => void;
    addSelectedSAL2080_R01List: (idList: string[]) => void;
    removeSelectedSAL2080_R01List: (idList?: string[]) => void;
    initSelectedSAL2080_R01List: () => void;

    // === AG Grid 컬럼 상태 관리 ===
    columnState: ColumnState[] | null;
    setColumnState: (state: ColumnState[] | null) => void;

    allCount: number;
    setAllCount: (allCount: number) => void;

    scrollTop: number | null;
    setScrollTop: (top: number | null) => void;

    // === 전체 초기화 ===
    initData: () => void;
}

/**
 * 기본 날짜 범위 (현재 월)
 */
const defaultDateRange: DateRange = getCurrentMonthDateRange();

export const defaultSAL2080_R01Filter: SAL2080_R01Filter = {
    compCd: '',
    customCd: '',
    customNm: '',
    taxType: '',
    taxBilling: '',
    issuedDateRange: defaultDateRange,
    remark: '',
    fixedFlag: '',
};

/**
 * Zustand 스토어 정의
 */
export const useSAL2080_R01Store = create<Store_SAL2080_R01>((set) => {
    /**
     * 공통 필터 설정 함수 생성
     */
    const createFilterSetter = (filterKey: keyof Pick<Store_SAL2080_R01, 'SAL2080_R01Filter' | 'SAL2080_R01PastFilter'>) => 
        (filter: Partial<SAL2080_R01Filter>) => set((state) => ({
            ...state,
            [filterKey]: { ...state[filterKey], ...filter }
        }));

    /**
     * 선택된 항목 제거 헬퍼 함수
     */
    const removeItemsFromArray = <T>(array: T[], itemsToRemove: T[]): T[] => {
        return array.filter(item => !itemsToRemove.includes(item));
    };

    return {
        SAL2080_R01Filter: defaultSAL2080_R01Filter,
        SAL2080_R01PastFilter: defaultSAL2080_R01Filter,
        setSAL2080_R01Filter: createFilterSetter('SAL2080_R01Filter'),
        setSAL2080_R01PastFilter: createFilterSetter('SAL2080_R01PastFilter'),

        // === 리스트 관련 ===
        SAL2080_R01List: [],
        setSAL2080_R01List: (list: SAL2080_R01Dto[]) => set({ SAL2080_R01List: list }),
        insertSAL2080_R01List: (data: SAL2080_R01Dto[], index?: number) => set((state) => {
            const _list = [...state.SAL2080_R01List];
            
            if (index !== undefined) {
                _list.splice(index, 0, ...data);
            } else {
                _list.push(...data);
            }
            return { ...state, SAL2080_R01List: _list };
        }),
        removeSAL2080_R01List: (deletedIndex: number) => set((state) => ({
            ...state,
            SAL2080_R01List: state.SAL2080_R01List.filter((_, index) => index !== deletedIndex),
        })),
        updateSAL2080_R01List: (data: SAL2080_R01Dto, index: number) => set((state) => {
            const _list = [...state.SAL2080_R01List];
            _list[index] = data;
            return { ...state, SAL2080_R01List: _list };
        }),

        // === 선택 관련 ===
        selectedSAL2080_R01List: [],
        setSelectedSAL2080_R01List: (idList: string[]) => set({ selectedSAL2080_R01List: idList }),
        addSelectedSAL2080_R01List: (idList: string[]) => set((state) => {
            const _selectedSAL2080_R01List = [...state.selectedSAL2080_R01List];
            _selectedSAL2080_R01List.push(...idList.filter(value => !_selectedSAL2080_R01List.includes(value)));
            return { ...state, selectedSAL2080_R01List: _selectedSAL2080_R01List };
        }),
        removeSelectedSAL2080_R01List: (idList?: string[]) => set((state) => {
            if (!idList) {
                return { ...state, selectedSAL2080_R01List: [] };
            }
            
            return {
                ...state,
                selectedSAL2080_R01List: removeItemsFromArray(state.selectedSAL2080_R01List, idList),
            };
        }),
        initSelectedSAL2080_R01List: () => set({ selectedSAL2080_R01List: [] }),
        
        // === AG Grid 컬럼 상태 관리 ===
        columnState: null,
        setColumnState: (state) => set({ columnState: state }),

        allCount: 0,
		
        setAllCount: (allCount: number) => set({ allCount }),

        scrollTop: null,
        setScrollTop: (top: number | null) => set({ scrollTop: top }),

        // === 전체 초기화 ===
        initData: () => set(() => ({
            SAL2080_R01Filter: defaultSAL2080_R01Filter,
            SAL2080_R01PastFilter: defaultSAL2080_R01Filter,
            SAL2080_R01List: [],
            selectedSAL2080_R01List: [],
            columnState: null,
            allCount: 0,
            scrollTop: null,
        })),
    };
});
